<template>
    <div class="container">
        <template v-if="loading"> <LoaderComponent></LoaderComponent> </template>

        <AccessDeniedBanner v-else-if="forbidden" :message="$t('equipement.accessDenied.message')" />

        <template v-else>
            <div class="title-container">
                <span>
                    {{ $t('equipementSelection.table.equipement') }}
                    <ReglePastille cle="equipementDetail.definition" />
                    <ReglePastille cle="equipementDetail.ongletsReutilises" />
                </span>
                <BorderedIconButton
                    v-if="!preavisLoading && !hasPreavisForEquipement"
                    :imgUrl="addImageUrl"
                    :on-click="openPreavisCreate"
                    :title="$t('modale.preavisIntervention.button.create')"
                    class="btn-preavis"
                />
                <ReglePastille cle="equipementDetail.preavisUnique" />
            </div>
            <h2 class="marginBottom">{{ detailsEquipement.libelle }}</h2>
            <div v-if="detailsEquipement.phaseLibelle && detailsEquipement.phaseLibelle.trim().toLowerCase() !== 'maintenance'" class="article-info">
                {{ detailsEquipement.phaseLibelle }}
            </div>
            <div class="content-container">
                <Tabs v-model:activeTabId="activeTabId" :tabs="tabs">
                    <DashboardTab
                        v-show="activeTabId === 'dashboard'"
                        :phase-id="phaseId"
                        :visible="activeTabId === 'dashboard'"
                    />
                    <HistoriqueDemandeInterventionsTab
                        v-if="activeTabId === 'historiqueDemandeInterventions'"
                        :phase-id="phaseId"
                    ></HistoriqueDemandeInterventionsTab>
                    <HistoriqueTravauxTab
                        v-if="activeTabId === 'historiqueTravaux'"
                        :phase-id="phaseId"
                    ></HistoriqueTravauxTab>
                    <InterventionsPreventivesTab
                        v-if="activeTabId === 'interventionsPreventives'"
                        :equipement="detailsEquipement"
                        :phase-id="phaseId"
                    ></InterventionsPreventivesTab>
                    <HistoriqueDevisTab
                        v-if="activeTabId === 'historiqueDevis'"
                        :phase-id="phaseId"
                    ></HistoriqueDevisTab>

                    <CaracteristiquesTab
                        v-if="activeTabId === 'caracteristiques'"
                        :phaseId="phaseId"
                    ></CaracteristiquesTab>
                    <MaintenancesTab v-if="activeTabId === 'gammeMaintenance'" :phase-id="phaseId"></MaintenancesTab>
                    <DocumentsReglementairesTab
                        v-if="activeTabId === 'docReglementaires'"
                        :phase-id="phaseId"
                    ></DocumentsReglementairesTab>
                </Tabs>
            </div>
        </template>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import Tabs from '@/modules/Equipements/components/Tabs.vue';
import CaracteristiquesTab from '@/modules/Equipements/components/CaracteristiquesTab.vue';
import MaintenancesTab from '@/modules/Equipements/components/MaintenancesTab.vue';
import DocumentsReglementairesTab from '@/modules/Equipements/components/DocumentsReglementairesTab.vue';
import HistoriqueDevisTab from '@/modules/Equipements/components/HistoriqueDevisTab.vue';
import HistoriqueTravauxTab from '@/modules/Equipements/components/HistoriqueTravauxTab.vue';
import { EquipementAction } from '@/modules/Equipements/store/actions-types';
import InterventionsPreventivesTab from '@/modules/Equipements/components/InterventionsPreventivesTab.vue';
import { EquipementItems } from '@/models/EquipementItems';
import { EQUIPEMENT } from '@/modules/Equipements/store/mutation-types';
import HistoriqueDemandeInterventionsTab from '@/modules/Equipements/components/HistoriqueDemandeInterventionsTab.vue';
import AccessDeniedBanner from '@/components/ui-components/AccessDeniedBanner.vue';
import LoaderComponent from '@/components/ui-components/LoaderComponent.vue';
import DashboardTab from '@/modules/Equipements/components/DashboardTab.vue';
import { EQUIPEMENT as EQUIPEMENT_INTERVENTION } from '@/modules/Intervention/store/mutation-types';
import { PREAVIS } from '@/modules/PreavisIntervention/store/mutation-types';
import BorderedIconButton from '@/components/ui-components/BorderedIconButton.vue';
import { PreavisInterventionDTO } from '@/models/PreavisIntervention/PreavisInterventionDTO';
import PreavisInterventionService from '@/modules/PreavisIntervention/services/PreavisInterventionService';
import ReglePastille from '@/components/regles/ReglePastille.vue';

export default defineComponent({
    name: 'EquipementsPage',
    components: {
        BorderedIconButton,
        LoaderComponent,
        AccessDeniedBanner,
        HistoriqueDemandeInterventionsTab,
        InterventionsPreventivesTab,
        HistoriqueTravauxTab,
        HistoriqueDevisTab,
        DocumentsReglementairesTab,
        MaintenancesTab,
        CaracteristiquesTab,
        Tabs,
        DashboardTab,
        ReglePastille,
    },
    data() {
        return {
            forbidden: false,
            loading: true,
            preavisLoading: true,
            equipementPreavis: [] as PreavisInterventionDTO[],
        };
    },
    computed: {
        hasPreavisForEquipement(): boolean {
            return this.equipementPreavis.some(
                (preavis: PreavisInterventionDTO) => preavis.equipement?.id == this.detailsEquipement.id
            );
        },
        addImageUrl() {
            return require('@/assets/img/add.svg');
        },

        tabs() {
            return this.$store.state.equipementState.tabs;
        },
        activeTabId: {
            get(): string {
                return this.$store.state.equipementState.activeTabId;
            },
            set(value: string) {
                this.$store.commit(`equipementState/${EQUIPEMENT.SET_ACTIVE_TAB}`, value);
            },
        },
        phaseId(): number {
            return Number(this.$route.params.id);
        },
        detailsEquipement(): EquipementItems {
            return this.$store.state.equipementState.detailsEquipement;
        },
    },
    methods: {
        openPreavisCreate() {
            const eq = this.detailsEquipement;
            this.$store.commit(`equipementModule/${EQUIPEMENT_INTERVENTION.SET_ITEMS}`, [eq]);
            this.$store.commit(`preavisInterventionState/${PREAVIS.SET_CREATE_DIRECT}`, {
                equipement: this.detailsEquipement,
                initialStep: 3,
            });
            this.$store.commit(`preavisInterventionState/${PREAVIS.SET_MODAL}`, true);
        },
        loadEquipementPreavis() {
            this.preavisLoading = true;
            PreavisInterventionService.getPreavisInterventions(
                undefined,
                [this.detailsEquipement?.id].filter(Boolean)
            )
                .then(res => {
                    this.equipementPreavis = res.data;
                })
                .catch(() => {
                    this.equipementPreavis = [];
                })
                .finally(() => {
                    this.preavisLoading = false;
                });
        },
    },
    watch: {
        '$store.state.preavisInterventionState.isModaleOpen'(isOpen: boolean) {
            if (!isOpen && this.detailsEquipement?.id) {
                this.loadEquipementPreavis();
            }
        },
    },
    mounted() {
        if (!this.phaseId) {
            this.loading = false;
            return;
        }

        this.$store
            .dispatch(`equipementState/${EquipementAction.GET_DETAILS_EQUIPEMENT}`, this.phaseId)
            .then(() => {
                this.loadEquipementPreavis();
            })
            .catch((err: any) => {
                if (err?.response?.status === 403) {
                    this.forbidden = true;
                    return;
                }
                throw err;
            })
            .finally(() => {
                this.loading = false;
            });
    },
});
</script>

<style scoped>
.content-container {
    flex: 1;
    min-height: 0;
    width: 100%;
}
.container {
    display: flex;
    flex-direction: column;
    flex: 1;
    padding: 24px 24px 0 24px;
    overflow-y: hidden;
}
.marginBottom {
    margin-bottom: 15px;
}
.article-info {
    font-size: 1.2rem;
    color: #666;
    margin-bottom: 15px;
}
.title-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
}
.btn-preavis {
    margin-left: auto;
}
</style>
